import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import WebApp from '@twa-dev/sdk';
import { useCartStore } from '../store/cart.store';
import { useCatalogStore } from '../store/catalog.store';
import { Product } from '../types/catalog';
import { ProductCard } from '../components/ProductCard';
import { BottomNavigation } from '../components/BottomNavigation';
import { AppFooter } from '../components/AppFooter';
import { useCustomerConfig } from '../hooks/useCustomerConfig';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import clsx from 'clsx';
import './Catalog.css';

export default function CatalogPage() {
  const navigate = useNavigate();
  const { config } = useCustomerConfig();

  const {
    loading,
    loadingMore,
    error,
    categories,
    selectedCategoryId,
    selectedCategorySlug,
    searchQuery,
    fetchCategories,
    fetchProducts,
    fetchMoreProducts,
    setCategory,
    setCategorySlug,
    setSearchQuery,
    minPrice,
    maxPrice,
    inStockOnly,
    sort,
    setPriceRange,
    setSort,
    products,
    hasMore,
  } = useCatalogStore();

  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);

  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [isFilterInputFocused, setIsFilterInputFocused] = useState(false);
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [minPriceInput, setMinPriceInput] = useState(minPrice != null ? String(minPrice) : '');
  const [maxPriceInput, setMaxPriceInput] = useState(maxPrice != null ? String(maxPrice) : '');
  const debouncedSearch = useDebouncedValue(searchQuery, 400);

  const applySearch = () => setSearchQuery(searchInput.trim());
  const applyPriceRange = () => {
    const min = minPriceInput.trim() ? Number(minPriceInput) : undefined;
    const max = maxPriceInput.trim() ? Number(maxPriceInput) : undefined;
    setPriceRange(min, max);
  };

  const sortLabels: Record<typeof sort, string> = {
    default: 'По умолчанию',
    newest: 'Сначала новые',
    price_asc: 'Цена ↑',
    price_desc: 'Цена ↓',
    oldest: 'Сначала старые',
  };
  const filterSummary =
    [minPrice != null ? `от ${minPrice}` : '', maxPrice != null ? `до ${maxPrice}` : ''].filter(Boolean).join(' · ') ||
    'Цена';
  const filterTriggerLabel = `${filterSummary} · ${sortLabels[sort]}`;

  useEffect(() => {
    // Use theme colors for Telegram UI
    const getThemeColor = (varName: string, fallback: `#${string}`): `#${string}` => {
      const value = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
      if (value && value.startsWith('#')) {
        return value as `#${string}`;
      }
      return fallback;
    };
    const bgColor = getThemeColor('--bg-main', '#FFFFFF');
    WebApp.setBackgroundColor(bgColor);
    WebApp.setHeaderColor(bgColor);
  }, []);

  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    setMinPriceInput(minPrice != null ? String(minPrice) : '');
    setMaxPriceInput(maxPrice != null ? String(maxPrice) : '');
  }, [minPrice, maxPrice]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    fetchProducts({ search: debouncedSearch });
  }, [selectedCategoryId, selectedCategorySlug, debouncedSearch, minPrice, maxPrice, inStockOnly, sort, fetchProducts]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !hasMore || loading || loadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) fetchMoreProducts();
      },
      { rootMargin: '200px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loading, loadingMore, fetchMoreProducts, products.length]);

  // Cart logic
  const addToCart = useCartStore((state) => state.addItem);
  const cartItems = useCartStore((state) => state.items);
  const cartTotal = useCartStore((state) => state.getTotal());
  const cartItemsCount = useCartStore((state) => state.getItemCount());

  const getProductQuantity = (productId: number) => {
    const item = cartItems.find((i) => i.productId === productId);
    return item ? item.quantity : 0;
  };

  const handleAddToCart = (product: Product, e: React.MouseEvent) => {
    e.stopPropagation();
    addToCart({
      productId: product.id,
      productName: product.name,
      price: product.price,
      quantity: 1,
      image: product.images?.[0],
      old_price: Number.isFinite(Number(product.old_price)) ? Number(product.old_price) : undefined,
    });
    // Visual feedback handled in component
  };

  const handleProductClick = (product: Product) => {
    navigate(`/product/${product.id}`);
  };

  // Loading Skeleton (only when no cached data)
  if (loading && !products.length) {
    return (
      <div className="catalog-page">
        <div className="container" style={{ paddingTop: '16px' }}>
          <div className="skeleton search-skeleton" />
          <div className="skeleton categories-skeleton" />
          <div className="catalog-skeleton-grid">
             {[1, 2, 3, 4, 5, 6].map(i => (
               <div key={i} className="skeleton catalog-skeleton-item" />
             ))}
          </div>
        </div>
        <BottomNavigation />
      </div>
    );
  }

  return (
    <div className="catalog-page" style={{ paddingBottom: '120px' }}>
      {/* Header / Search */}
      <div className="catalog-header">
        <div className="catalog-brand">
          <img
            src="/logo.jpg"
            alt={config?.brand?.displayName || 'Бери Охапку'}
            className="catalog-brand__logo"
          />
          <div className="catalog-brand__name">
            {config?.brand?.displayName || 'Бери Охапку'}
          </div>
        </div>
        <div className="search-wrapper">
          <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
          <input
            type="search"
            placeholder="Поиск букетов..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySearch()}
            onFocus={() => setIsFilterInputFocused(true)}
            onBlur={() => {
              setIsFilterInputFocused(false);
              applySearch();
            }}
            className="catalog-search"
          />
        </div>
      </div>

      {/* Категории с API или fallback: Все товары, Букеты, Подарки, Цветы */}
      <div className="catalog-categories no-scrollbar">
        {categories.length > 0
          ? categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => {
                  setCategory(cat.id === 0 ? undefined : cat.id);
                  setCategorySlug('all');
                }}
                className={clsx(
                  'catalog-chip',
                  (cat.id === 0 ? (selectedCategoryId == null || selectedCategoryId === 0) : selectedCategoryId === cat.id) &&
                    'catalog-chip--active'
                )}
              >
                {cat.name}
              </button>
            ))
          : [
              { slug: 'all' as const, label: 'Все товары' },
              { slug: 'bouquets' as const, label: 'Букеты' },
              { slug: 'gifts' as const, label: 'Подарки' },
              { slug: 'flowers' as const, label: 'Цветы' },
            ].map(({ slug, label }) => (
              <button
                key={slug}
                type="button"
                onClick={() => {
                  setCategory(undefined);
                  setCategorySlug(slug);
                }}
                className={clsx('catalog-chip', selectedCategorySlug === slug && 'catalog-chip--active')}
              >
                {label}
              </button>
            ))}
      </div>

      {/* Filters: раскрывающийся блок */}
      <div className="catalog-filters-wrap">
        <button
          type="button"
          className="catalog-filters-trigger"
          onClick={() => setFilterPanelOpen((v) => !v)}
          aria-expanded={filterPanelOpen}
        >
          <span className="catalog-filters-trigger__label">{filterTriggerLabel}</span>
          <svg
            className={clsx('catalog-filters-trigger__chevron', filterPanelOpen && 'catalog-filters-trigger__chevron--open')}
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        <div className={clsx('catalog-filters-panel', filterPanelOpen && 'catalog-filters-panel--open')}>
          <div className="catalog-filters">
            <div>
              <div className="catalog-filter-label">Цена от</div>
              <input
                type="number"
                min={0}
                className="catalog-filter-input"
                value={minPriceInput}
                onChange={(e) => setMinPriceInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyPriceRange()}
                onFocus={() => setIsFilterInputFocused(true)}
                onBlur={() => {
                  setIsFilterInputFocused(false);
                  applyPriceRange();
                }}
                placeholder="от"
              />
            </div>
            <div>
              <div className="catalog-filter-label">Цена до</div>
              <input
                type="number"
                min={0}
                className="catalog-filter-input"
                value={maxPriceInput}
                onChange={(e) => setMaxPriceInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && applyPriceRange()}
                onFocus={() => setIsFilterInputFocused(true)}
                onBlur={() => {
                  setIsFilterInputFocused(false);
                  applyPriceRange();
                }}
                placeholder="до"
              />
            </div>
            <div className="catalog-filter-sort">
              <div className="catalog-filter-label">Сортировка</div>
              <select
                className="catalog-filter-select"
                value={sort}
                onChange={(e) => setSort(e.target.value as typeof sort)}
              >
                <option value="default">По умолчанию</option>
                <option value="newest">Сначала новые</option>
                <option value="price_asc">Цена ↑</option>
                <option value="price_desc">Цена ↓</option>
                <option value="oldest">Сначала старые</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="container catalog-container">
        {products.length === 0 ? (
          <div className="catalog-empty">
            <div className="catalog-empty__icon">🥀</div>
            <h3>{searchQuery.trim() ? 'Ничего не найдено' : 'Товаров в каталоге пока нет'}</h3>
            <p>{searchQuery.trim() ? 'Попробуйте изменить запрос' : 'Скоро здесь появятся новинки'}</p>
            {!searchQuery.trim() && (
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => fetchProducts()}
                disabled={loading}
                style={{ marginTop: '12px' }}
              >
                {loading ? 'Загрузка…' : 'Обновить каталог'}
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="catalog-grid">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onClick={() => handleProductClick(product)}
                  onAdd={(e) => handleAddToCart(product, e)}
                  countInCart={getProductQuantity(product.id)}
                />
              ))}
            </div>
            <div ref={loadMoreSentinelRef} className="catalog-load-more-sentinel" aria-hidden />
            {loadingMore && (
              <div className="catalog-loading-more" style={{ textAlign: 'center', padding: '12px', fontSize: '13px', color: 'var(--text-tertiary)' }}>
                Загрузка…
              </div>
            )}
          </>
        )}
      </div>

      {/* Data freshness banner and background refresh error */}
      <div className="container" style={{ paddingTop: '8px', paddingBottom: '8px' }}>
        {error && products.length > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            padding: '8px 12px',
            borderRadius: '8px',
            backgroundColor: 'var(--bg-secondary)',
            marginBottom: '8px',
          }}>
            <span style={{ fontSize: '13px', color: 'var(--color-error)' }}>
              Не удалось обновить
            </span>
            <button
              type="button"
              onClick={() => fetchProducts()}
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: 'var(--color-accent)',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px 8px',
              }}
            >
              Повторить
            </button>
          </div>
        )}
        <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', textAlign: 'center' }}>
          {loading && products.length > 0 ? 'Обновление…' : null}
        </div>
      </div>

      {/* Floating Cart Button — скрываем при вводе в поиск/цену, чтобы не перекрывать клавиатуру */}
      {cartItemsCount > 0 && !isFilterInputFocused && (
        <div className="floating-cart-wrapper">
          <button
            onClick={() => navigate('/cart')}
            className="floating-cart"
          >
            <span className="floating-cart__label">Корзина</span>
            <span className="floating-cart__price">{cartTotal.toLocaleString('ru-RU')} ₽</span>
          </button>
        </div>
      )}

      <AppFooter />
      {!isFilterInputFocused && <BottomNavigation />}
    </div>
  );
}
